import React from 'react';
import { Modal, Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

const BmkvdPreviewModal = ({ open, onClose, previewData, loading, error, onDownload, downloading }) => {
    const { t ,i18n} = useTranslation();
    const direction = i18n.dir();

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '90%', md: 800 },
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 2,
        p: 4,
        overflow: 'auto'
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('he-IL', {
            style: 'currency',
            currency: 'ILS',
            minimumFractionDigits: 2
        }).format(amount);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="bmkvd-preview-modal"
            aria-describedby="bmkvd-preview-description"
        >
            <Box sx={modalStyle} dir={direction}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" component="h2">
                        {t('UniformFilePreview')}
                    </Typography>
                    <Button onClick={onClose} sx={{ minWidth: 'auto' }}>
                        <CloseIcon />
                    </Button>
                </Box>

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Error State */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {!loading && error && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button variant="outlined" onClick={onClose}>
                            {t('Close')}
                        </Button>
                    </Box>
                )}

                {/* Preview Content */}
                {!loading && !error && previewData && (
                    <>
                        {/* Client Info */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body1" gutterBottom>
                                <strong>{t('ClientName')}:</strong> {previewData.clientName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>{t('CaseId')}:</strong> {previewData.caseId}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>{t('Period')}:</strong> {new Date(previewData.startDate).toLocaleDateString('he-IL')} - {new Date(previewData.endDate).toLocaleDateString('he-IL')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>{t('FilePath')}:</strong> {previewData.filePath}
                            </Typography>
                        </Box>

                        {/* Record Counts Summary */}
                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                            {t('RecordCounts')}
                        </Typography>
                        <TableContainer component={Paper} sx={{ mb: 3 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>{t('RecordType')}</strong></TableCell>
                                        <TableCell align="right"><strong>{t('Count')}</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>A100 (פתיחה)</TableCell>
                                        <TableCell align="right">{previewData.recordCounts.a100Count}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>C100 (כותרת מסמך)</TableCell>
                                        <TableCell align="right">{previewData.recordCounts.c100Count}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>D110 (פרטי מסמך)</TableCell>
                                        <TableCell align="right">{previewData.recordCounts.d110Count}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>D120 (פרטי קבלה/הפקדה)</TableCell>
                                        <TableCell align="right">{previewData.recordCounts.d120Count}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>B110 (חשבונות)</TableCell>
                                        <TableCell align="right">{previewData.recordCounts.b110Count}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>B100 (תנועות חשבונאיות)</TableCell>
                                        <TableCell align="right">{previewData.recordCounts.b100Count}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>M100 (מלאי)</TableCell>
                                        <TableCell align="right">{previewData.recordCounts.m100Count}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Z900 (סגירה)</TableCell>
                                        <TableCell align="right">{previewData.recordCounts.z900Count}</TableCell>
                                    </TableRow>
                                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell><strong>{t('TotalRecords')}</strong></TableCell>
                                        <TableCell align="right"><strong>{previewData.totalRecords}</strong></TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Document Type Summaries */}
                        {previewData.documentTypeSummaries && previewData.documentTypeSummaries.length > 0 && (
                            <>
                                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                    {t('DocumentTypeSummaries')}
                                </Typography>
                                <TableContainer component={Paper} sx={{ mb: 3 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>{t('DocumentTypeCode')}</strong></TableCell>
                                                <TableCell><strong>{t('DocumentTypeName')}</strong></TableCell>
                                                <TableCell align="right"><strong>{t('Count')}</strong></TableCell>
                                                <TableCell align="right"><strong>{t('TotalAmount')}</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {previewData.documentTypeSummaries.map((docType, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{docType.documentTypeCode}</TableCell>
                                                    <TableCell>{docType.documentTypeName}</TableCell>
                                                    <TableCell align="right">{docType.count}</TableCell>
                                                    <TableCell align="right">{formatCurrency(docType.totalAmount)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                            <Button variant="outlined" onClick={onClose}>
                                {t('Close')}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                                onClick={onDownload}
                                disabled={downloading}
                            >
                                {downloading ? t('Downloading') : t('DownloadUniformFile')}
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default BmkvdPreviewModal;
